import type { Table } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import clsx from "clsx";

import styles from "./PatientsTable.module.scss";
import type {
  IArea,
  IPatientType,
  ITeam,
  IWard,
} from "@medoma/models/patients";

export type IPatientsTableItem<T> = {
  notifications: string;
  familyName: string;
  givenName: string;
  patient: T;
  ward: IWard;
  personalIdentityNumber: string;
  patientType: IPatientType;
  area: IArea | null | undefined;
  team: ITeam | null | undefined;
};

export const PatientsTable = <T,>({ table }: { table: Table<T> }) => {
  return (
    <div className={styles.tableWrapper}>
      <table className={styles.patientsTable}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const isSorted = header.column.getIsSorted();

                return (
                  <th
                    className={clsx(
                      styles.tableHeaderCell,
                      // Apply column-specific styles based on column id (e.g., styles.notifications, styles.familyName)
                      styles[header.column.id],
                    )}
                    key={header.id}
                    aria-sort={
                      isSorted
                        ? isSorted === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        className={styles.sortButton}
                        onClick={header.column.getToggleSortingHandler()}
                        type="button"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        <span
                          className={styles.sortIndicator}
                          aria-hidden="true"
                        >
                          {isSorted === "asc"
                            ? " ▲"
                            : isSorted === "desc"
                              ? " ▼"
                              : ""}
                        </span>
                      </button>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  // Apply column-specific styles based on column id (e.g., styles.notifications, styles.patient)
                  className={styles[cell.column.id]}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
